import React, { useState, useEffect } from 'react';
import {
  BellSlashIcon,
  ClockIcon,
  TrashIcon,
  PlusIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  TableCellsIcon,
  ChartBarIcon,
  CheckBadgeIcon,
  LinkIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import snoozeService from '../../services/snoozeService';
import SnoozeModal from '../../components/notifications/SnoozeModal';

// `icon` holds the Heroicon component itself (not an instance) so
// consumers can size + color it inline. Mirrors the SnoozeModal config
// for visual consistency across the snooze surface area.
const SNOOZE_TYPE_LABELS = {
  table: { label: 'Table', icon: TableCellsIcon, color: 'bg-blue-100 text-blue-800' },
  metric: { label: 'Metric', icon: ChartBarIcon, color: 'bg-green-100 text-green-800' },
  validation_rule: { label: 'Validation', icon: CheckBadgeIcon, color: 'bg-purple-100 text-purple-800' },
  connection: { label: 'Connection', icon: LinkIcon, color: 'bg-orange-100 text-orange-800' },
  pattern: { label: 'Pattern', icon: MagnifyingGlassIcon, color: 'bg-yellow-100 text-yellow-800' },
};

const formatTimeRemaining = (snoozedUntil) => {
  const until = new Date(snoozedUntil);
  const now = new Date();
  const diffMs = until - now;

  if (diffMs <= 0) return 'Expired';

  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) return `${diffDays}d ${diffHours % 24}h remaining`;
  if (diffHours > 0) return `${diffHours}h ${diffMins % 60}m remaining`;
  return `${diffMins}m remaining`;
};

const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleString();
};

const SnoozeSettings = () => {
  const [snoozes, setSnoozes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchSnoozes = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await snoozeService.getActiveSnoozes();
      setSnoozes(response.snoozes || []);
    } catch (err) {
      setError(err.message || 'Failed to load snooze rules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSnoozes();
  }, []);

  const handleCancelSnooze = async (snoozeId) => {
    setDeletingId(snoozeId);
    try {
      await snoozeService.cancelSnooze(snoozeId);
      setSnoozes(snoozes.filter(s => s.id !== snoozeId));
    } catch (err) {
      setError(err.message || 'Failed to cancel snooze');
    } finally {
      setDeletingId(null);
    }
  };

  const handleExtendSnooze = async (snoozeId, additionalMinutes) => {
    try {
      await snoozeService.extendSnooze(snoozeId, additionalMinutes);
      fetchSnoozes(); // Refresh to get updated times
    } catch (err) {
      setError(err.message || 'Failed to extend snooze');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900 flex items-center">
            <BellSlashIcon className="h-6 w-6 text-gray-400 mr-2" />
            Snoozed Alerts
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage temporarily silenced notifications. Snoozes automatically expire.
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchSnoozes}
            disabled={loading}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <ArrowPathIcon className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            New Snooze Rule
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Snooze List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <ArrowPathIcon className="h-8 w-8 text-gray-400 animate-spin" />
        </div>
      ) : snoozes.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <BellSlashIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-sm font-medium text-gray-900">No active snoozes</h3>
          <p className="mt-2 text-sm text-gray-500">
            All your alerts are currently active. Snooze alerts from the issues page or create a rule here.
          </p>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <ul className="divide-y divide-gray-200">
            {snoozes.map((snooze) => {
              const typeInfo = SNOOZE_TYPE_LABELS[snooze.snooze_type] || SNOOZE_TYPE_LABELS.pattern;
              const isExpired = new Date(snooze.snoozed_until) < new Date();

              return (
                <li key={snooze.id} className={`p-4 ${isExpired ? 'bg-gray-50' : ''}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center min-w-0">
                      {typeInfo.icon && (
                        <typeInfo.icon className="h-6 w-6 text-gray-500 mr-3 flex-shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center space-x-2">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {snooze.target_name || snooze.target_id}
                          </p>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${typeInfo.color}`}>
                            {typeInfo.label}
                          </span>
                        </div>
                        <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                          <span className="flex items-center">
                            <ClockIcon className="h-4 w-4 mr-1" />
                            {isExpired ? (
                              <span className="text-red-500">Expired</span>
                            ) : (
                              formatTimeRemaining(snooze.snoozed_until)
                            )}
                          </span>
                          {snooze.snooze_reason && (
                            <span className="truncate">
                              Reason: {snooze.snooze_reason}
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-xs text-gray-400">
                          Until {formatDate(snooze.snoozed_until)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      {!isExpired && (
                        <div className="relative">
                          <select
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value) {
                                handleExtendSnooze(snooze.id, parseInt(value, 10));
                                e.target.value = '';
                              }
                            }}
                            className="text-sm border-gray-300 rounded-md pr-8 focus:ring-indigo-500 focus:border-indigo-500"
                            defaultValue=""
                          >
                            <option value="" disabled>Extend...</option>
                            <option value="60">+1 hour</option>
                            <option value="240">+4 hours</option>
                            <option value="1440">+1 day</option>
                            <option value="10080">+1 week</option>
                          </select>
                        </div>
                      )}
                      <button
                        onClick={() => handleCancelSnooze(snooze.id)}
                        disabled={deletingId === snooze.id}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                      >
                        <TrashIcon className="h-4 w-4 mr-1" />
                        {deletingId === snooze.id ? 'Removing...' : 'Remove'}
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Pattern Snooze Info */}
      <div className="rounded-md bg-blue-50 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <span className="text-xl"></span>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Pro tip: Pattern-based snoozing</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                Use pattern snoozes to silence multiple related alerts at once. For example:
              </p>
              <ul className="mt-2 list-disc list-inside space-y-1">
                <li><code className="bg-blue-100 px-1 rounded">dbt_test_*</code> - Snooze all dbt test alerts</li>
                <li><code className="bg-blue-100 px-1 rounded">staging_*</code> - Snooze all staging table alerts</li>
                <li><code className="bg-blue-100 px-1 rounded">*_backup</code> - Snooze all backup table alerts</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateSnoozeModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            fetchSnoozes();
          }}
        />
      )}
    </div>
  );
};

// Simple modal for creating pattern-based snoozes
const CreateSnoozeModal = ({ isOpen, onClose, onCreated }) => {
  const [snoozeType, setSnoozeType] = useState('pattern');
  const [targetId, setTargetId] = useState('');
  const [targetName, setTargetName] = useState('');
  const [durationKey, setDurationKey] = useState('24h');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!targetId.trim()) {
      setError('Target ID/pattern is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await snoozeService.quickSnooze({
        snooze_type: snoozeType,
        target_id: targetId.trim(),
        target_name: targetName.trim() || targetId.trim(),
        duration_key: durationKey,
        reason: reason.trim() || undefined
      });
      onCreated();
    } catch (err) {
      setError(err.message || 'Failed to create snooze');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
        <div className="relative w-full max-w-md bg-white rounded-lg shadow-xl p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Create Snooze Rule</h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Snooze Type
              </label>
              <select
                value={snoozeType}
                onChange={(e) => setSnoozeType(e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
                <option value="pattern">Pattern (wildcards supported)</option>
                <option value="table">Table Name</option>
                <option value="metric">Metric Name</option>
                <option value="validation_rule">Validation Rule ID</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {snoozeType === 'pattern' ? 'Pattern' : 'Target ID'}
              </label>
              <input
                type="text"
                value={targetId}
                onChange={(e) => setTargetId(e.target.value)}
                placeholder={snoozeType === 'pattern' ? 'e.g., dbt_test_*' : 'Enter target name or ID'}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
              {snoozeType === 'pattern' && (
                <p className="mt-1 text-xs text-gray-500">
                  Use * for wildcards. Example: staging_* matches staging_users, staging_orders
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Display Name (optional)
              </label>
              <input
                type="text"
                value={targetName}
                onChange={(e) => setTargetName(e.target.value)}
                placeholder="Human-readable name"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duration
              </label>
              <select
                value={durationKey}
                onChange={(e) => setDurationKey(e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
                <option value="1h">1 hour</option>
                <option value="4h">4 hours</option>
                <option value="24h">24 hours</option>
                <option value="3d">3 days</option>
                <option value="1w">1 week</option>
                <option value="2w">2 weeks</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason (optional)
              </label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Planned maintenance"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
              >
                {isSubmitting ? 'Creating...' : 'Create Snooze'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SnoozeSettings;
